import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MODALIDADE, NIVEL_TURMA, type Modalidade, type NivelTurma } from '@arena/shared/dominio';
import {
  CAPACIDADE_PADRAO,
  ehModalidadePersonalizada,
  ROTULO_MODALIDADE,
  ROTULO_NIVEL_TURMA,
  zNovaTurma,
  type ConsultaDeDisponibilidade,
  type ModalidadeAlocavel,
} from '@arena/shared/schemas';
import { dataValida, MINUTOS_DO_DIA } from '@arena/shared/tempo';
import { agendaApi } from '../api/agenda.js';
import { mensagemDoErro, ErroDeApi } from '../api/cliente.js';
import { turmasApi } from '../api/turmas.js';
import { horaCurta, rotuloDeDuracao } from '../agenda/formatos.js';
import { janelaDaGrade } from '../agenda/layout.js';
import {
  AvisoDeDisponibilidade,
  errosPorCampo,
  opcoesDeInicio,
  primeiraMensagem,
} from '../agenda/ModalDeReserva.jsx';
import { useAtrasado } from '../agenda/useAtrasado.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Escolhas, Formulario, Nota } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { diasDaTurma } from './formatos.js';

/**
 * `#m-turma` — cadastro de turma, com a sugestão de horário candidato.
 *
 * Salvar cria a turma E a série dela na agenda, numa transação só: "Turma
 * criada. A grade das próximas semanas foi gerada na agenda." Se a grade
 * conflita, a turma não nasce — e a verificação ao vivo, a mesma de
 * `#m-reserva`, avisa antes.
 *
 * A quadra pode ficar "pela alocação fixa": quando a pendência P1 já tem
 * alocação para aquele horário, é ela que decide.
 *
 * O "Status" do protótipo não está no cadastro: turma nasce ativa, e
 * suspender ou encerrar exige motivo — é caso de uso na tela da turma, não
 * um campo livre.
 */

const DIAS_RAPIDOS: readonly { rotulo: string; dias: readonly number[] }[] = [
  { rotulo: 'Terça e quinta', dias: [2, 4] },
  { rotulo: 'Segunda e quarta', dias: [1, 3] },
  { rotulo: 'Sábado', dias: [6] },
];

const NOMES_DOS_DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const DURACOES = [60, 90, 120];

export function ModalDeTurma({
  onFechar,
  onCriada,
}: {
  onFechar: () => void;
  onCriada: (turmaId: string) => void;
}) {
  const grade = useQuery({
    queryKey: ['agenda', 'grade', 'hoje'],
    queryFn: () => agendaApi.grade(),
  });
  const professores = useQuery({
    queryKey: ['turmas', 'professores'],
    queryFn: () => turmasApi.professores(),
  });

  if (grade.isPending || professores.isPending) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Cadastro de turma">
        <CorpoDeModal>
          <EsqueletoDeCard linhas={5} />
        </CorpoDeModal>
      </Modal>
    );
  }
  if (grade.isError || professores.isError) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Cadastro de turma" largura="estreita">
        <CorpoDeModal>
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{mensagemDoErro(grade.error ?? professores.error)}</div>
          </div>
        </CorpoDeModal>
      </Modal>
    );
  }

  return (
    <FormularioDeTurma
      grade={grade.data}
      professores={professores.data.filter((p) => p.ativo)}
      onFechar={onFechar}
      onCriada={onCriada}
    />
  );
}

function FormularioDeTurma({
  grade,
  professores,
  onFechar,
  onCriada,
}: {
  grade: Awaited<ReturnType<typeof agendaApi.grade>>;
  professores: Awaited<ReturnType<typeof turmasApi.professores>>;
  onFechar: () => void;
  onCriada: (turmaId: string) => void;
}) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();

  const janela = janelaDaGrade(grade.horarioFuncionamento);
  // Inícios que deixam ao menos uma hora de aula antes de a arena fechar —
  // e não passam da meia-noite: a série da turma anda em dias da semana do
  // calendário, e a arena que fecha às 02:00 não tem turma "às 25:00".
  const inicios = opcoesDeInicio(janela.abertura, Math.min(janela.fechamento - 30, MINUTOS_DO_DIA));

  const [modalidade, setModalidade] = useState<Modalidade>('Futvolei');
  // O nome livre da modalidade personalizada — "ritmos", "treino de
  // saque". Só existe em `Outra`, e é o que o título da turma mostra.
  const [modalidadeOutra, setModalidadeOutra] = useState('');
  const personalizada = ehModalidadePersonalizada(modalidade);
  const [niveis, setNiveis] = useState<NivelTurma[]>(['Iniciante']);
  const [professorId, setProfessorId] = useState(professores[0]?.id ?? '');
  const [novoProfessor, setNovoProfessor] = useState<string | null>(null);
  const [quadraId, setQuadraId] = useState<string>(grade.quadras[0]?.id ?? '');
  const [dias, setDias] = useState<number[]>([2, 4]);
  const [horaInicio, setHoraInicio] = useState(
    inicios.includes('20:00') ? '20:00' : (inicios[0] ?? '07:00'),
  );
  const [duracao, setDuracao] = useState(60);
  const [capacidade, setCapacidade] = useState(String(CAPACIDADE_PADRAO));
  const [inicio, setInicio] = useState('');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  // A sugestão de horário olha a ocupação e a fila DA MODALIDADE, e a
  // alocação fixa não aceita a personalizada (ver MODALIDADES_ALOCAVEIS):
  // em "Outra" não há o que sugerir, e a consulta nem sai.
  const sugestao = useQuery({
    queryKey: ['turmas', 'sugestao', modalidade],
    queryFn: () => turmasApi.sugestao(modalidade as ModalidadeAlocavel),
    enabled: pode('turmas', 'criar') && !personalizada,
    retry: false,
  });

  // A mesma verificação de `#m-reserva`: a série inteira, data a data.
  const consulta: ConsultaDeDisponibilidade = useAtrasado(
    {
      quadraId,
      data: inicio && dataValida(inicio) ? inicio : grade.data,
      inicio: horaInicio,
      duracaoMinutos: duracao,
      recorrencia: { diasSemana: dias },
    },
    350,
  );
  const disponibilidade = useQuery({
    queryKey: ['agenda', 'disponibilidade', 'turma', consulta],
    queryFn: () => agendaApi.disponibilidade(consulta),
    enabled: Boolean(quadraId) && dias.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 0,
    retry: false,
  });

  const criarProfessor = useMutation({
    mutationFn: (nome: string) => turmasApi.criarProfessor({ nome }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['turmas', 'professores'] }),
  });

  const criar = useMutation({
    mutationFn: turmasApi.criar,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['turmas'] });
      void cliente.invalidateQueries({ queryKey: ['agenda'] });
    },
  });

  function alternarNivel(nivel: NivelTurma) {
    setNiveis((atuais) =>
      atuais.includes(nivel) ? atuais.filter((n) => n !== nivel) : [...atuais, nivel],
    );
  }

  function alternarDia(dia: number) {
    setDias((atuais) =>
      atuais.includes(dia)
        ? atuais.filter((d) => d !== dia)
        : [...atuais, dia].sort((a, b) => a - b),
    );
  }

  async function salvarProfessor() {
    if (!novoProfessor?.trim()) return;
    try {
      const criado = await criarProfessor.mutateAsync(novoProfessor.trim());
      setProfessorId(criado.id);
      setNovoProfessor(null);
      avisar(`${criado.nome} cadastrado como professor.`);
    } catch (e) {
      setErros({ professorId: mensagemDoErro(e) });
    }
  }

  async function salvar() {
    setErros({});
    setErroGeral(null);
    const pedido = zNovaTurma.safeParse({
      modalidade,
      ...(personalizada ? { modalidadeOutra: modalidadeOutra.trim() } : {}),
      niveis,
      professorId,
      ...(quadraId ? { quadraId } : {}),
      diasSemana: dias,
      horaInicio,
      duracaoMinutos: duracao,
      capacidade: Number(capacidade),
      ...(inicio ? { inicio } : {}),
    });
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }
    try {
      const resultado = await criar.mutateAsync(pedido.data);
      avisar('Turma criada. A grade das próximas semanas foi gerada na agenda.');
      onCriada(resultado.turma.id);
    } catch (e) {
      if (e instanceof ErroDeApi && e.campos) setErros(primeiraMensagem(e.campos));
      setErroGeral(mensagemDoErro(e));
    }
  }

  const s = sugestao.data;

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Cadastro de turma"
      descricao={`Capacidade padrão de ${CAPACIDADE_PADRAO} alunos, com lista de espera automática.`}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void salvar()} disabled={criar.isPending}>
            {criar.isPending ? 'Gerando a grade…' : 'Criar turma'}
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
          <Campo rotulo="Modalidade" erro={erros['modalidade']}>
            <select
              value={modalidade}
              onChange={(e) => setModalidade(e.target.value as Modalidade)}
            >
              {MODALIDADE.map((m) => (
                <option key={m} value={m}>
                  {m === 'Outra' ? 'Outra — digitar o nome' : ROTULO_MODALIDADE[m]}
                </option>
              ))}
            </select>
          </Campo>
          {personalizada && (
            <Campo
              rotulo="Nome da modalidade"
              ajuda="Aparece no título da turma, na agenda e nos relatórios."
              erro={erros['modalidadeOutra']}
            >
              <input
                autoFocus
                maxLength={40}
                placeholder="Ritmos, treino de saque…"
                value={modalidadeOutra}
                onChange={(e) => setModalidadeOutra(e.target.value)}
              />
            </Campo>
          )}
          <Campo rotulo="Nível" erro={erros['niveis']}>
            <Escolhas
              aria-label="Nível"
              opcoes={NIVEL_TURMA.map((n) => ({ valor: n, rotulo: ROTULO_NIVEL_TURMA[n] }))}
              valor={niveis}
              onChange={alternarNivel}
            />
          </Campo>

          <Campo rotulo="Professor" erro={erros['professorId']}>
            {novoProfessor === null ? (
              <select
                value={professorId}
                onChange={(e) => {
                  if (e.target.value === '__novo') setNovoProfessor('');
                  else setProfessorId(e.target.value);
                }}
              >
                {professores.length === 0 && <option value="">Nenhum professor cadastrado</option>}
                {professores.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
                {pode('turmas', 'criar') && <option value="__novo">+ Cadastrar professor…</option>}
              </select>
            ) : (
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  autoFocus
                  placeholder="Nome do professor"
                  value={novoProfessor}
                  onChange={(e) => setNovoProfessor(e.target.value)}
                />
                <Botao
                  pequeno
                  onClick={() => void salvarProfessor()}
                  disabled={criarProfessor.isPending}
                >
                  Salvar
                </Botao>
                <Botao variante="fantasma" pequeno onClick={() => setNovoProfessor(null)}>
                  Voltar
                </Botao>
              </div>
            )}
          </Campo>
          <Campo
            rotulo="Quadra"
            erro={erros['quadraId']}
            ajuda={quadraId ? undefined : 'A alocação fixa daquele horário decide a quadra.'}
          >
            <select value={quadraId} onChange={(e) => setQuadraId(e.target.value)}>
              {grade.quadras.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.nome} · {q.superficie}
                </option>
              ))}
              <option value="">Pela alocação fixa (P1)</option>
            </select>
          </Campo>

          <Campo
            rotulo="Dias"
            erro={erros['diasSemana']}
            ajuda={dias.length > 0 ? diasDaTurma(dias) : undefined}
          >
            <Escolhas
              aria-label="Dias da semana"
              opcoes={NOMES_DOS_DIAS.map((nome, dia) => ({ valor: String(dia), rotulo: nome }))}
              valor={dias.map(String)}
              onChange={(dia) => alternarDia(Number(dia))}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {DIAS_RAPIDOS.map((r) => (
                <button
                  key={r.rotulo}
                  type="button"
                  className="link"
                  onClick={() => setDias([...r.dias])}
                >
                  {r.rotulo}
                </button>
              ))}
            </div>
          </Campo>
          <Campo rotulo="Horário" erro={erros['horaInicio']}>
            <div style={{ display: 'flex', gap: 6 }}>
              <select value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)}>
                {inicios.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
              <select
                aria-label="Duração"
                value={duracao}
                onChange={(e) => setDuracao(Number(e.target.value))}
              >
                {DURACOES.map((m) => (
                  <option key={m} value={m}>
                    {rotuloDeDuracao(m)}
                  </option>
                ))}
              </select>
            </div>
          </Campo>

          <Campo rotulo="Capacidade" erro={erros['capacidade']}>
            <input
              type="number"
              min={1}
              max={40}
              value={capacidade}
              onChange={(e) => setCapacidade(e.target.value)}
            />
          </Campo>
          <Campo
            rotulo="Primeira aula a partir de"
            erro={erros['inicio']}
            ajuda="Em branco: a próxima aula possível, hoje ou amanhã."
          >
            <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
          </Campo>
        </Formulario>

        <div aria-live="polite">
          {quadraId && dias.length > 0 && (
            <AvisoDeDisponibilidade resultado={disponibilidade.data} />
          )}
        </div>

        {s && (
          <Nota>
            O horário das <b>{horaCurta(s.hora)}</b> de {diasDaTurma(s.diasSemana).toLowerCase()} na{' '}
            {s.quadra.nome} está com {s.ocupacao}% de ocupação
            {s.esperando > 0 ? (
              <>
                {' '}
                e há{' '}
                <b>
                  {s.esperando === 1 ? '1 pessoa' : `${s.esperando} pessoas`} na lista de espera
                </b>
                {s.turmaComFila ? ` do ${s.turmaComFila.toLowerCase()}` : ''}
              </>
            ) : null}
            . É o melhor candidato para uma turma nova.{' '}
            <button
              type="button"
              className="link"
              onClick={() => {
                setDias([...s.diasSemana]);
                setHoraInicio(s.hora);
                setQuadraId(s.quadra.id);
                setDuracao(60);
              }}
            >
              Usar este horário
            </button>
          </Nota>
        )}

        {erroGeral && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erroGeral}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
